import { CurrentWeather, WeatherCondition } from '@/types/enhancedTrail';
import { Coordinates } from '@/types/hikingTrail';

// Default cache settings (used if env vars not defined)
const DEFAULT_CACHE_TIME_MS = 10 * 60 * 1000; // 10 minutes in milliseconds
const DEFAULT_CACHE_DISTANCE_KM = 5; // 5 kilometers

// Cache configuration from environment variables
const WEATHER_CACHE_TIME_MS = parseInt(
  process.env.NEXT_PUBLIC_WEATHER_CACHE_TIME_MS ||
    String(DEFAULT_CACHE_TIME_MS),
  10
);
const WEATHER_CACHE_DISTANCE_KM = parseFloat(
  process.env.NEXT_PUBLIC_WEATHER_CACHE_DISTANCE_KM ||
    String(DEFAULT_CACHE_DISTANCE_KM)
);

// Engelberg coordinates for default weather queries
export const ENGELBERG_COORDINATES: Coordinates = {
  latitude: 46.8182,
  longitude: 8.4093,
};

// Weather API Response Types
export interface WeatherConditionAPI {
  id: number;
  main: string;
  description: string;
  icon: string;
}

export interface OpenWeatherResponse {
  coord: {
    lon: number;
    lat: number;
  };
  weather: WeatherConditionAPI[];
  base: string;
  main: {
    temp: number;
    feels_like: number;
    temp_min: number;
    temp_max: number;
    pressure: number;
    humidity: number;
  };
  visibility: number;
  wind: {
    speed: number;
    deg: number;
    gust?: number;
  };
  clouds: {
    all: number;
  };
  rain?: {
    '1h'?: number;
    '3h'?: number;
  };
  snow?: {
    '1h'?: number;
    '3h'?: number;
  };
  dt: number; // Time of data calculation, unix, UTC
  sys: {
    type: number;
    id: number;
    country: string;
    sunrise: number;
    sunset: number;
  };
  timezone: number;
  id: number;
  name: string;
  cod: number;
}

export interface ForecastItem {
  dt: number; // Time of data forecasted, unix, UTC
  main: {
    temp: number;
    feels_like: number;
    temp_min: number;
    temp_max: number;
    pressure: number;
    sea_level: number;
    grnd_level: number;
    humidity: number;
    temp_kf: number;
  };
  weather: WeatherConditionAPI[];
  clouds: {
    all: number;
  };
  wind: {
    speed: number;
    deg: number;
    gust?: number;
  };
  visibility: number;
  pop: number; // Probability of precipitation
  rain?: {
    '3h': number;
  };
  snow?: {
    '3h': number;
  };
  sys: {
    pod: string; // Part of day (d = day, n = night)
  };
  dt_txt: string; // Time of data forecasted, ISO, UTC
}

export interface ForecastResponse {
  cod: string;
  message: number;
  cnt: number;
  list: ForecastItem[];
  city: {
    id: number;
    name: string;
    coord: {
      lat: number;
      lon: number;
    };
    country: string;
    population: number;
    timezone: number;
    sunrise: number;
    sunset: number;
  };
}

// Error codes for the weather API
export enum WeatherApiErrorCode {
  AUTH_ERROR = 'auth_error',
  AUTH_COOLDOWN = 'auth_cooldown',
  NETWORK_ERROR = 'network_error',
  API_ERROR = 'api_error',
  INVALID_DATA = 'invalid_data',
}

// Custom error type for the weather API
export interface WeatherApiError {
  code: WeatherApiErrorCode;
  message: string;
  retryAfter?: number; // Milliseconds until retry is possible
}

/**
 * API Provider enum to track which API is currently being used
 */
export enum WeatherApiProvider {
  OPENWEATHER = 'openweather',
  VISUALCROSSING = 'visualcrossing',
}

/**
 * Interface for cached weather data
 */
interface CachedWeatherData {
  coordinates: Coordinates;
  timestamp: number;
  data: OpenWeatherResponse | ForecastResponse | null;
}

/**
 * Interface for weather cache
 */
interface WeatherCache {
  currentWeather: CachedWeatherData[];
  forecast: CachedWeatherData[];
}

/**
 * Counter object interface for tracking API calls
 */
interface ApiCallCounter {
  openWeatherMapCalls: number;
  visualCrossingCalls: number;
  lastReset: number; // Timestamp for tracking when the counters were last reset
}

/**
 * Interface for Weather API providers
 */
interface IWeatherApiProvider {
  readonly name: WeatherApiProvider;
  readonly maxCallsPerDay: number;

  isReady(): boolean;
  getCurrentWeather(
    coordinates: Coordinates
  ): Promise<OpenWeatherResponse | null>;
  getForecast(coordinates: Coordinates): Promise<ForecastResponse | null>;
  getIconUrl(iconCode: string): string;
  formatTemperature(temp: number): string;
  formatTime(timestamp: number): string;
  formatDate(timestamp: number): string;
  formatWindSpeed(speed: number): string;
  getAuthCooldownStatus(): { inCooldown: boolean; timeRemaining: number };
}

/**
 * Base class for weather API providers with common functionality
 */
abstract class BaseWeatherProvider implements IWeatherApiProvider {
  abstract readonly name: WeatherApiProvider;
  abstract readonly maxCallsPerDay: number;

  protected lastAuthErrorTime: number | null = null;
  protected readonly authCooldownMs: number;
  protected hasLoggedCooldownWarning = false;

  constructor(cooldownMs: number) {
    this.authCooldownMs = cooldownMs;
  }

  /**
   * Check if we're in an auth error cooldown period
   * @returns True if in cooldown period, false otherwise
   */
  protected isInAuthCooldown(): boolean {
    if (!this.lastAuthErrorTime) return false;

    const now = Date.now();
    const timeElapsed = now - this.lastAuthErrorTime;
    return timeElapsed < this.authCooldownMs;
  }

  /**
   * Check if the service is currently in an authorization cooldown period
   * @returns Object with cooldown status and time remaining
   */
  public getAuthCooldownStatus(): {
    inCooldown: boolean;
    timeRemaining: number;
  } {
    if (!this.lastAuthErrorTime) {
      return { inCooldown: false, timeRemaining: 0 };
    }

    const now = Date.now();
    const timeElapsed = now - this.lastAuthErrorTime;
    const timeRemaining = Math.max(0, this.authCooldownMs - timeElapsed);

    return {
      inCooldown: timeRemaining > 0,
      timeRemaining,
    };
  }

  /**
   * Format temperature as string with unit
   */
  public formatTemperature(temp: number): string {
    return `${Math.round(temp)}°C`;
  }

  /**
   * Format timestamp as time
   */
  public formatTime(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Format timestamp as date
   */
  public formatDate(timestamp: number): string {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Format wind speed with unit
   */
  public formatWindSpeed(speed: number): string {
    return `${Math.round(speed)} m/s`;
  }

  /**
   * Get weather icon URL
   */
  public getIconUrl(iconCode: string): string {
    return `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
  }

  /**
   * Whether the provider is ready (has API key, etc.)
   */
  abstract isReady(): boolean;

  /**
   * Get current weather data
   */
  abstract getCurrentWeather(
    coordinates: Coordinates
  ): Promise<OpenWeatherResponse | null>;

  /**
   * Get forecast data
   */
  abstract getForecast(
    coordinates: Coordinates
  ): Promise<ForecastResponse | null>;
}

/**
 * OpenWeatherMap API Provider Implementation
 */
class OpenWeatherMapProvider extends BaseWeatherProvider {
  readonly name = WeatherApiProvider.OPENWEATHER;
  readonly maxCallsPerDay = 1000;

  private readonly API_KEY: string;
  private readonly BASE_URL = 'https://api.openweathermap.org/data/2.5';
  private readonly UNITS = 'metric';

  constructor() {
    super(
      parseInt(
        process.env.NEXT_PUBLIC_OPENWEATHER_AUTH_COOLDOWN_MS || '60000',
        10
      )
    );
    this.API_KEY = process.env.NEXT_PUBLIC_OPENWEATHER_API_KEY || '';

    if (!this.API_KEY) {
      console.warn(
        'OpenWeatherMap API key is not set. Weather data may not be available.'
      );
    }
  }

  /**
   * Check if the provider is ready to use
   */
  public isReady(): boolean {
    return !!this.API_KEY && this.API_KEY !== 'YOUR_OPENWEATHERMAP_API_KEY';
  }

  /**
   * Get current weather from OpenWeatherMap API
   */
  async getCurrentWeather(
    coordinates: Coordinates
  ): Promise<OpenWeatherResponse | null> {
    if (!this.isReady()) {
      console.error('OpenWeatherMap API key is not set');
      return null;
    }

    // Check if we're in cooldown period for auth errors
    if (this.isInAuthCooldown()) {
      // Only log the warning once during a cooldown period
      if (!this.hasLoggedCooldownWarning) {
        const { timeRemaining } = this.getAuthCooldownStatus();
        console.warn(
          `In OpenWeatherMap authorization cooldown period. Try again in ${Math.ceil(timeRemaining / 1000)} seconds.`
        );
        this.hasLoggedCooldownWarning = true;

        // Set a timeout to reset the warning flag when cooldown expires
        setTimeout(() => {
          this.hasLoggedCooldownWarning = false;
        }, timeRemaining);
      }
      return null;
    }

    try {
      const response = await fetch(
        `${this.BASE_URL}/weather?lat=${coordinates.latitude}&lon=${coordinates.longitude}&units=${this.UNITS}&appid=${this.API_KEY}`
      );

      if (!response.ok) {
        // Check for 401 Unauthorized status
        if (response.status === 401) {
          console.error(
            'Authorization failed with OpenWeatherMap API. Setting cooldown period.'
          );
          this.lastAuthErrorTime = Date.now();
          this.hasLoggedCooldownWarning = true; // Set flag to prevent repeated warnings
          throw new Error('Weather API authorization failed (401)');
        }
        throw new Error(
          `OpenWeatherMap API returned status ${response.status}`
        );
      }

      const data: OpenWeatherResponse = await response.json();
      return data;
    } catch (error) {
      console.error(
        'Error fetching current weather from OpenWeatherMap:',
        error
      );
      return null;
    }
  }

  /**
   * Get forecast data from OpenWeatherMap API
   */
  async getForecast(
    coordinates: Coordinates
  ): Promise<ForecastResponse | null> {
    if (!this.isReady()) {
      console.error('OpenWeatherMap API key is not set');
      return null;
    }

    // Check if we're in cooldown period for auth errors
    if (this.isInAuthCooldown()) {
      // Only log the warning once during a cooldown period
      if (!this.hasLoggedCooldownWarning) {
        const { timeRemaining } = this.getAuthCooldownStatus();
        console.warn(
          `In OpenWeatherMap authorization cooldown period. Try again in ${Math.ceil(timeRemaining / 1000)} seconds.`
        );
        this.hasLoggedCooldownWarning = true;

        // Set a timeout to reset the warning flag when cooldown expires
        setTimeout(() => {
          this.hasLoggedCooldownWarning = false;
        }, timeRemaining);
      }
      return null;
    }

    try {
      const response = await fetch(
        `${this.BASE_URL}/forecast?lat=${coordinates.latitude}&lon=${coordinates.longitude}&units=${this.UNITS}&appid=${this.API_KEY}`
      );

      if (!response.ok) {
        // Check for 401 Unauthorized status
        if (response.status === 401) {
          console.error(
            'Authorization failed with OpenWeatherMap API. Setting cooldown period.'
          );
          this.lastAuthErrorTime = Date.now();
          this.hasLoggedCooldownWarning = true; // Set flag to prevent repeated warnings
          throw new Error('Weather API authorization failed (401)');
        }
        throw new Error(
          `OpenWeatherMap API returned status ${response.status}`
        );
      }

      const data: ForecastResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching forecast from OpenWeatherMap:', error);
      return null;
    }
  }
}

/**
 * Visual Crossing API Provider Implementation
 */
class VisualCrossingProvider extends BaseWeatherProvider {
  readonly name = WeatherApiProvider.VISUALCROSSING;
  readonly maxCallsPerDay = 1000;

  private readonly API_KEY: string;
  private readonly BASE_URL =
    'https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline';

  constructor() {
    super(
      parseInt(
        process.env.NEXT_PUBLIC_VISUALCROSSING_AUTH_COOLDOWN_MS || '60000',
        10
      )
    );
    this.API_KEY = process.env.NEXT_PUBLIC_VISUALCROSSING_API_KEY || '';

    if (!this.API_KEY) {
      console.warn(
        'Visual Crossing API key is not set. Fallback weather data may not be available.'
      );
    }
  }

  /**
   * Check if the provider is ready to use
   */
  public isReady(): boolean {
    return !!this.API_KEY && this.API_KEY !== 'YOUR_VISUALCROSSING_API_KEY';
  }

  /**
   * Get current weather from Visual Crossing API
   */
  async getCurrentWeather(
    coordinates: Coordinates
  ): Promise<OpenWeatherResponse | null> {
    if (!this.isReady()) {
      console.error('Visual Crossing API key is not set');
      return null;
    }

    // Check if we're in cooldown period for auth errors
    if (this.isInAuthCooldown()) {
      // Only log the warning once during a cooldown period
      if (!this.hasLoggedCooldownWarning) {
        const { timeRemaining } = this.getAuthCooldownStatus();
        console.warn(
          `In Visual Crossing authorization cooldown period. Try again in ${Math.ceil(timeRemaining / 1000)} seconds.`
        );
        this.hasLoggedCooldownWarning = true;

        // Set a timeout to reset the warning flag when cooldown expires
        setTimeout(() => {
          this.hasLoggedCooldownWarning = false;
        }, timeRemaining);
      }
      return null;
    }

    try {
      // Visual Crossing Timeline API request
      const response = await fetch(
        `${this.BASE_URL}/${coordinates.latitude},${coordinates.longitude}/today?unitGroup=metric&include=current&key=${this.API_KEY}&contentType=json`
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.error(
            'Authorization failed with Visual Crossing API. Setting cooldown period.'
          );
          this.lastAuthErrorTime = Date.now();
          this.hasLoggedCooldownWarning = true;
          throw new Error('Visual Crossing API authorization failed');
        }
        // Handle rate limit errors (429 Too Many Requests)
        if (response.status === 429) {
          console.error(
            'Rate limit exceeded with Visual Crossing API. Setting cooldown period.'
          );
          this.lastAuthErrorTime = Date.now();
          this.hasLoggedCooldownWarning = true;
          throw new Error('Visual Crossing API rate limit exceeded (429)');
        }
        throw new Error(
          `Visual Crossing API returned status ${response.status}`
        );
      }

      // Parse Visual Crossing response
      const vcData = await response.json();

      // Transform to match OpenWeatherMap format for compatibility
      const weatherData: OpenWeatherResponse = {
        coord: {
          lon: coordinates.longitude,
          lat: coordinates.latitude,
        },
        weather: [
          {
            id: 800, // Default ID
            main: vcData.currentConditions.conditions || 'Unknown',
            description:
              vcData.currentConditions.conditions || 'Unknown conditions',
            icon: this.getOpenWeatherIconFromVC(vcData.currentConditions.icon),
          },
        ],
        base: 'Visual Crossing',
        main: {
          temp: vcData.currentConditions.temp,
          feels_like: vcData.currentConditions.feelslike,
          temp_min: vcData.days[0].tempmin,
          temp_max: vcData.days[0].tempmax,
          pressure: vcData.currentConditions.pressure,
          humidity: vcData.currentConditions.humidity,
        },
        visibility: vcData.currentConditions.visibility * 1000, // Convert to meters
        wind: {
          speed: vcData.currentConditions.windspeed,
          deg: vcData.currentConditions.winddir,
          gust: vcData.currentConditions.windgust,
        },
        clouds: {
          all: vcData.currentConditions.cloudcover,
        },
        dt: Math.floor(
          new Date(vcData.currentConditions.datetime).getTime() / 1000
        ),
        sys: {
          type: 1,
          id: 1,
          country: vcData.address?.split(',').pop()?.trim() || '',
          sunrise: Math.floor(
            new Date(vcData.days[0].sunrise).getTime() / 1000
          ),
          sunset: Math.floor(new Date(vcData.days[0].sunset).getTime() / 1000),
        },
        timezone: vcData.timezone,
        id: 0,
        name: vcData.resolvedAddress?.split(',')[0] || 'Unknown',
        cod: 200,
      };

      // Add precipitation data if available
      if (vcData.currentConditions.precip > 0) {
        weatherData.rain = { '1h': vcData.currentConditions.precip };
      }

      // Add snow data if temperature is below freezing and there's precipitation
      if (
        vcData.currentConditions.temp < 0 &&
        vcData.currentConditions.precip > 0
      ) {
        weatherData.snow = { '1h': vcData.currentConditions.precip };
      }

      return weatherData;
    } catch (error) {
      console.error(
        'Error fetching current weather from Visual Crossing:',
        error
      );
      return null;
    }
  }

  /**
   * Get forecast data from Visual Crossing API
   */
  async getForecast(
    coordinates: Coordinates
  ): Promise<ForecastResponse | null> {
    if (!this.isReady()) {
      console.error('Visual Crossing API key is not set');
      return null;
    }

    // Check if we're in cooldown period for auth errors
    if (this.isInAuthCooldown()) {
      // Only log the warning once during a cooldown period
      if (!this.hasLoggedCooldownWarning) {
        const { timeRemaining } = this.getAuthCooldownStatus();
        console.warn(
          `In Visual Crossing authorization cooldown period. Try again in ${Math.ceil(timeRemaining / 1000)} seconds.`
        );
        this.hasLoggedCooldownWarning = true;

        // Set a timeout to reset the warning flag when cooldown expires
        setTimeout(() => {
          this.hasLoggedCooldownWarning = false;
        }, timeRemaining);
      }
      return null;
    }

    try {
      // Visual Crossing Timeline API request for forecast
      const response = await fetch(
        `${this.BASE_URL}/${coordinates.latitude},${coordinates.longitude}/next5days?unitGroup=metric&include=hours&key=${this.API_KEY}&contentType=json`
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.error(
            'Authorization failed with Visual Crossing API. Setting cooldown period.'
          );
          this.lastAuthErrorTime = Date.now();
          this.hasLoggedCooldownWarning = true;
          throw new Error('Visual Crossing API authorization failed');
        }
        // Handle rate limit errors (429 Too Many Requests)
        if (response.status === 429) {
          console.error(
            'Rate limit exceeded with Visual Crossing API. Setting cooldown period.'
          );
          this.lastAuthErrorTime = Date.now();
          this.hasLoggedCooldownWarning = true;
          throw new Error('Visual Crossing API rate limit exceeded (429)');
        }
        throw new Error(
          `Visual Crossing API returned status ${response.status}`
        );
      }

      // Parse Visual Crossing response
      const vcData = await response.json();

      // Transform to match OpenWeatherMap format for compatibility
      const forecastItems: ForecastItem[] = [];

      // Process each day and its hours
      vcData.days.forEach((day: any) => {
        if (day.hours && Array.isArray(day.hours)) {
          // Process 3-hour intervals (to match OpenWeatherMap's 3-hour forecast)
          for (let i = 0; i < day.hours.length; i += 3) {
            const hour = day.hours[i];
            if (!hour) continue;

            const date = new Date(`${day.datetime}T${hour.datetime}`);

            const forecastItem: ForecastItem = {
              dt: Math.floor(date.getTime() / 1000),
              main: {
                temp: hour.temp,
                feels_like: hour.feelslike,
                temp_min: hour.temp - 1, // Approximated
                temp_max: hour.temp + 1, // Approximated
                pressure: hour.pressure,
                sea_level: hour.pressure,
                grnd_level: hour.pressure,
                humidity: hour.humidity,
                temp_kf: 0,
              },
              weather: [
                {
                  id: 800, // Default ID
                  main: hour.conditions || 'Unknown',
                  description: hour.conditions || 'Unknown conditions',
                  icon: this.getOpenWeatherIconFromVC(hour.icon),
                },
              ],
              clouds: {
                all: hour.cloudcover,
              },
              wind: {
                speed: hour.windspeed,
                deg: hour.winddir,
                gust: hour.windgust,
              },
              visibility: hour.visibility * 1000, // Convert to meters
              pop: hour.precipprob / 100, // Convert percentage to decimal
              sys: {
                pod: date.getHours() >= 6 && date.getHours() < 18 ? 'd' : 'n', // Day or night based on hour
              },
              dt_txt: date.toISOString(),
            };

            // Add precipitation data if available
            if (hour.precip > 0) {
              forecastItem.rain = { '3h': hour.precip };
            }

            // Add snow data if temperature is below freezing and there's precipitation
            if (hour.temp < 0 && hour.precip > 0) {
              forecastItem.snow = { '3h': hour.precip };
            }

            forecastItems.push(forecastItem);
          }
        }
      });

      // Create forecast response object
      const forecastResponse: ForecastResponse = {
        cod: '200',
        message: 0,
        cnt: forecastItems.length,
        list: forecastItems,
        city: {
          id: 0,
          name: vcData.resolvedAddress?.split(',')[0] || 'Unknown',
          coord: {
            lat: coordinates.latitude,
            lon: coordinates.longitude,
          },
          country: vcData.address?.split(',').pop()?.trim() || '',
          population: 0,
          timezone: vcData.timezone * 3600, // Convert to seconds
          sunrise: Math.floor(
            new Date(vcData.days[0].sunrise).getTime() / 1000
          ),
          sunset: Math.floor(new Date(vcData.days[0].sunset).getTime() / 1000),
        },
      };

      return forecastResponse;
    } catch (error) {
      console.error('Error fetching forecast from Visual Crossing:', error);
      return null;
    }
  }

  /**
   * Map Visual Crossing icon codes to OpenWeatherMap icon codes
   */
  private getOpenWeatherIconFromVC(iconCode: string): string {
    // Visual Crossing icon mapping to OpenWeatherMap equivalents
    const iconMap: { [key: string]: string } = {
      'clear-day': '01d',
      'clear-night': '01n',
      'partly-cloudy-day': '02d',
      'partly-cloudy-night': '02n',
      cloudy: '03d',
      rain: '10d',
      snow: '13d',
      sleet: '13d',
      wind: '50d',
      fog: '50d',
      thunderstorm: '11d',
    };

    return iconMap[iconCode] || '01d'; // Default to clear day if no match
  }
}

/**
 * Helper function to calculate distance between two coordinates in kilometers
 */
function calculateDistanceInKm(
  coord1: Coordinates,
  coord2: Coordinates
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((coord2.latitude - coord1.latitude) * Math.PI) / 180;
  const dLon = ((coord2.longitude - coord1.longitude) * Math.PI) / 180;

  // Haversine formula
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((coord1.latitude * Math.PI) / 180) *
      Math.cos((coord2.latitude * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Helper function to map weather condition from API to our app's WeatherCondition type
 */
function mapWeatherCondition(weatherMain: string): WeatherCondition {
  // Map OpenWeatherMap or Visual Crossing conditions to our app's WeatherCondition type
  switch (weatherMain.toLowerCase()) {
    case 'clear':
      return 'sunny';
    case 'clouds':
    case 'cloudy':
    case 'partly cloudy':
      return 'cloudy';
    case 'rain':
    case 'drizzle':
    case 'thunderstorm':
      return 'rainy';
    case 'snow':
      return 'snowy';
    case 'mist':
    case 'fog':
    case 'haze':
      return 'foggy';
    case 'wind':
    case 'squall':
    case 'tornado':
      return 'windy';
    default:
      // Default to sunny if we can't map the condition
      return 'sunny';
  }
}

/**
 * Main Weather API Service that manages providers and call counting
 */
class WeatherApiService {
  // Provider instances
  private readonly openWeatherProvider: OpenWeatherMapProvider;
  private readonly visualCrossingProvider: VisualCrossingProvider;

  // Current active provider
  private activeProvider: IWeatherApiProvider;

  // Counter initialization
  private callCounterInitialized = false;
  
  // In-memory API call counter (replaces Firebase counter)
  private apiCallCounter: ApiCallCounter = {
    openWeatherMapCalls: 0,
    visualCrossingCalls: 0,
    lastReset: Date.now()
  };

  // Weather data cache
  private cache: WeatherCache = {
    currentWeather: [],
    forecast: [],
  };

  // Track API calls that are already in progress to prevent duplicates
  private inProgressCalls: Map<string, Promise<any>> = new Map();

  // Maximum size for cache arrays
  private readonly MAX_CACHE_SIZE = 20;

  constructor() {
    // Initialize providers
    this.openWeatherProvider = new OpenWeatherMapProvider();
    this.visualCrossingProvider = new VisualCrossingProvider();

    // Default to OpenWeatherMap if available, otherwise try Visual Crossing
    this.activeProvider = this.openWeatherProvider.isReady()
      ? this.openWeatherProvider
      : this.visualCrossingProvider;

    // Initialize API call counter
    this.initializeApiCallCounter();

    console.log(
      `Weather cache initialized with ${WEATHER_CACHE_TIME_MS / 1000}s cache time and ${WEATHER_CACHE_DISTANCE_KM}km radius`
    );
  }

  /**
   * Initialize the API call counter (in-memory implementation)
   */
  private async initializeApiCallCounter() {
    try {
      // Check if we should reset counters (daily reset)
      const now = Date.now();
      const oneDayMs = 24 * 60 * 60 * 1000;

      if (now - this.apiCallCounter.lastReset > oneDayMs) {
        // Reset counters if more than a day has passed
        this.apiCallCounter = {
          openWeatherMapCalls: 0,
          visualCrossingCalls: 0,
          lastReset: now
        };

        console.log('API call counters have been reset.');

        // Always start with OpenWeatherMap after reset
        this.activeProvider = this.openWeatherProvider.isReady()
          ? this.openWeatherProvider
          : this.visualCrossingProvider;
      } else {
        // Determine which provider to use based on call counts
        if (
          this.apiCallCounter.openWeatherMapCalls >=
            this.openWeatherProvider.maxCallsPerDay &&
          this.visualCrossingProvider.isReady()
        ) {
          this.activeProvider = this.visualCrossingProvider;
          console.log(
            'Using Visual Crossing API as OpenWeatherMap daily limit reached'
          );
        } else if (
          this.apiCallCounter.openWeatherMapCalls <
            this.openWeatherProvider.maxCallsPerDay &&
          this.openWeatherProvider.isReady()
        ) {
          this.activeProvider = this.openWeatherProvider;
          console.log('Using OpenWeatherMap API (within daily limit)');
        }
      }

      this.callCounterInitialized = true;
    } catch (error) {
      console.error('Failed to initialize API call counter:', error);
      // Default to OpenWeatherMap if there's an error
      this.activeProvider = this.openWeatherProvider.isReady()
        ? this.openWeatherProvider
        : this.visualCrossingProvider;
    }
  }

  /**
   * Generate a cache key based on coordinates and API type
   * @param coordinates - The coordinates to fetch data for
   * @param callType - The type of API call ('current' or 'forecast')
   * @returns A unique string key for the coordinates and call type
   */
  private generateCacheKey(
    coordinates: Coordinates,
    callType: 'current' | 'forecast'
  ): string {
    return `${callType}-${coordinates.latitude.toFixed(4)},${coordinates.longitude.toFixed(4)}`;
  }

  /**
   * Increment the API call counter for a specific provider (in-memory implementation)
   * @param provider - The provider whose counter should be incremented
   * @returns True if provider was switched due to reaching limits, false otherwise
   */
  private async incrementApiCallCounter(
    provider: WeatherApiProvider = this.activeProvider.name
  ): Promise<boolean> {
    try {
      // Log which provider is being used
      console.log(`Using weather provider: ${provider}`);

      if (provider === WeatherApiProvider.OPENWEATHER) {
        // Increment OpenWeatherMap counter
        this.apiCallCounter.openWeatherMapCalls++;
        const currentCount = this.apiCallCounter.openWeatherMapCalls;

        console.log(
          `OpenWeatherMap API call made. Current count: ${currentCount}/${this.openWeatherProvider.maxCallsPerDay}`
        );

        // Switch to Visual Crossing if OpenWeatherMap limit reached
        if (
          currentCount >= this.openWeatherProvider.maxCallsPerDay &&
          this.visualCrossingProvider.isReady()
        ) {
          this.activeProvider = this.visualCrossingProvider;
          console.log(
            'Switching to Visual Crossing API after reaching OpenWeatherMap daily limit'
          );
          return true; // Switched provider
        }
      } else if (provider === WeatherApiProvider.VISUALCROSSING) {
        // Increment Visual Crossing counter
        this.apiCallCounter.visualCrossingCalls++;
        const currentCount = this.apiCallCounter.visualCrossingCalls;

        console.log(
          `Visual Crossing API call made. Current count: ${currentCount}/${this.visualCrossingProvider.maxCallsPerDay}`
        );
      }

      return false; // No provider switch
    } catch (error) {
      console.error('Failed to update API call counter:', error);
      return false;
    }
  }

  /**
   * Get the current active provider name
   */
  public getCurrentProviderName(): WeatherApiProvider {
    return this.activeProvider.name;
  }

  /**
   * Check if the service is currently in an authorization cooldown period
   */
  public getAuthCooldownStatus(): {
    inCooldown: boolean;
    timeRemaining: number;
  } {
    return this.activeProvider.getAuthCooldownStatus();
  }

  /**
   * Check if there's a valid cached weather data for given coordinates
   */
  private getCachedWeatherData(
    coordinates: Coordinates,
    cacheArray: CachedWeatherData[]
  ): CachedWeatherData | null {
    const now = Date.now();

    // Look for a matching cache entry
    for (const entry of cacheArray) {
      // Check if the cache entry is still valid (not expired)
      const ageMs = now - entry.timestamp;
      if (ageMs > WEATHER_CACHE_TIME_MS) {
        continue; // Skip expired entries
      }

      // Check if location is within acceptable distance
      const distance = calculateDistanceInKm(coordinates, entry.coordinates);
      if (distance <= WEATHER_CACHE_DISTANCE_KM) {
        console.log(
          `Cache hit! Using data from ${Math.round(distance * 1000)}m away, ${Math.round(ageMs / 1000)}s old`
        );
        return entry;
      }
    }

    return null; // No valid cache entry found
  }

  /**
   * Add data to the weather cache
   */
  private addToCache(
    cacheArray: CachedWeatherData[],
    coordinates: Coordinates,
    data: OpenWeatherResponse | ForecastResponse | null
  ): void {
    // Add new entry to beginning of array
    cacheArray.unshift({
      coordinates,
      timestamp: Date.now(),
      data,
    });

    // Limit cache size
    if (cacheArray.length > this.MAX_CACHE_SIZE) {
      cacheArray.pop(); // Remove oldest entry
    }
  }

  /**
   * Fetch current weather data for a location
   */
  async getCurrentWeatherData(
    coordinates: Coordinates
  ): Promise<OpenWeatherResponse | null> {
    console.log(
      `getCurrentWeather requested for ${coordinates.latitude},${coordinates.longitude}`
    );

    // Create a cache key for this request
    const cacheKey = this.generateCacheKey(coordinates, 'current');

    // Check if there's already an in-progress call for the same coordinates
    const existingCall = this.inProgressCalls.get(cacheKey);
    if (existingCall) {
      console.log(
        `Reusing in-progress weather request for ${coordinates.latitude},${coordinates.longitude}`
      );
      return existingCall as Promise<OpenWeatherResponse | null>;
    }

    // Check memory cache first
    const cachedData = this.getCachedWeatherData(
      coordinates,
      this.cache.currentWeather
    );
    if (cachedData) {
      return cachedData.data as OpenWeatherResponse | null;
    }

    // Not in cache, create a new API call promise
    console.log(
      `No cache hit, fetching from API with provider: ${this.activeProvider.name}`
    );

    // Create a promise for the API call
    const weatherPromise = (async () => {
      // Wait for counter initialization if not ready
      if (!this.callCounterInitialized) {
        try {
          await this.initializeApiCallCounter();
        } catch (error) {
          console.error('Failed to initialize API counter:', error);
        }
      }

      let result: OpenWeatherResponse | null = null;

      // If we're using OpenWeatherMap and Visual Crossing is available as fallback
      if (
        this.activeProvider.name === WeatherApiProvider.OPENWEATHER &&
        this.visualCrossingProvider.isReady()
      ) {
        // First try with OpenWeatherMap
        let openWeatherResult: OpenWeatherResponse | null = null;
        let openWeatherError: any = null;

        try {
          console.log(
            'Attempting to fetch weather data from OpenWeatherMap...'
          );
          openWeatherResult =
            await this.openWeatherProvider.getCurrentWeather(coordinates);

          // Check if result is valid
          if (openWeatherResult) {
            console.log('OpenWeatherMap data fetch successful!');
            const switched = await this.incrementApiCallCounter();

            // If provider was switched due to reaching OpenWeatherMap limit,
            // try again with Visual Crossing
            if (switched) {
              console.log('Switched to Visual Crossing due to API limit');
              result =
                await this.visualCrossingProvider.getCurrentWeather(
                  coordinates
                );
            } else {
              result = openWeatherResult;
            }
          } else {
            // If no result but also no error, still switch to Visual Crossing
            console.warn(
              'OpenWeatherMap returned null without error, switching to Visual Crossing'
            );
            openWeatherError = new Error('OpenWeatherMap returned null result');
            throw openWeatherError;
          }
        } catch (error: any) {
          openWeatherError = error;
          console.warn(
            `OpenWeatherMap API error: ${error.message || 'Unknown error'}`
          );

          // If OpenWeatherMap fails, try Visual Crossing as fallback
          console.log(
            'Switching to Visual Crossing due to OpenWeatherMap failure'
          );
          this.activeProvider = this.visualCrossingProvider;

          try {
            console.log(
              'Attempting to fetch weather data from Visual Crossing...'
            );
            const visualCrossingResult =
              await this.visualCrossingProvider.getCurrentWeather(coordinates);

            if (visualCrossingResult) {
              console.log('Visual Crossing data fetch successful!');
              await this.incrementApiCallCounter();
              result = visualCrossingResult;
            } else {
              console.error(
                'Both weather providers failed. OpenWeatherMap error:',
                openWeatherError
              );
              console.error(
                'Visual Crossing returned null without throwing error'
              );
              result = null;
            }
          } catch (visualCrossingError) {
            console.error('Both weather providers failed.');
            console.error('OpenWeatherMap error:', openWeatherError);
            console.error('Visual Crossing error:', visualCrossingError);
            result = null;
          }
        }
      }
      // If we're already using Visual Crossing or OpenWeatherMap doesn't have a fallback
      else {
        try {
          console.log(`Using ${this.activeProvider.name} for weather data...`);
          // Try current active provider
          result = await this.activeProvider.getCurrentWeather(coordinates);

          // If successful, increment counter
          if (result) {
            console.log(`${this.activeProvider.name} data fetch successful!`);
            await this.incrementApiCallCounter();
          } else {
            console.warn(
              `${this.activeProvider.name} returned null without error`
            );

            // If Visual Crossing fails and OpenWeatherMap is available, try switching back
            if (
              this.activeProvider.name === WeatherApiProvider.VISUALCROSSING &&
              this.openWeatherProvider.isReady()
            ) {
              console.log(
                'Visual Crossing failed. Trying OpenWeatherMap as last resort...'
              );
              this.activeProvider = this.openWeatherProvider;
              result =
                await this.openWeatherProvider.getCurrentWeather(coordinates);
            }
          }
        } catch (error) {
          console.error(
            `Error with ${this.activeProvider.name} provider:`,
            error
          );

          // If Visual Crossing fails and OpenWeatherMap is available, try switching back
          if (
            this.activeProvider.name === WeatherApiProvider.VISUALCROSSING &&
            this.openWeatherProvider.isReady()
          ) {
            console.log(
              'Visual Crossing failed. Trying OpenWeatherMap as last resort...'
            );
            this.activeProvider = this.openWeatherProvider;
            result =
              await this.openWeatherProvider.getCurrentWeather(coordinates);
          } else {
            result = null;
          }
        }
      }

      // Add result to cache
      this.addToCache(this.cache.currentWeather, coordinates, result);

      // Remove this call from in-progress tracking
      this.inProgressCalls.delete(cacheKey);

      return result;
    })();

    // Store the promise in the in-progress calls map
    this.inProgressCalls.set(cacheKey, weatherPromise);

    // Return the promise
    return weatherPromise;
  }

  /**
   * Transform OpenWeatherMap/VisualCrossing API response to our app's CurrentWeather format
   */
  mapToCurrentWeather(data: OpenWeatherResponse): CurrentWeather {
    // Determine weather condition from API response
    const condition = mapWeatherCondition(data.weather[0].main);

    // Determine if it's raining or snowing
    const isRaining =
      data.weather[0].main.toLowerCase().includes('rain') ||
      data.weather[0].main.toLowerCase().includes('drizzle') ||
      (data.rain && (data.rain['1h'] ?? 0) > 0);

    const isSnowing =
      data.weather[0].main.toLowerCase().includes('snow') ||
      (data.snow && (data.snow['1h'] ?? 0) > 0);

    // Format sunrise and sunset times
    const formatTimeStr = (timestamp: number): string => {
      const date = new Date(timestamp * 1000);
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    // Create CurrentWeather object
    return {
      condition,
      temperature: Math.round(data.main.temp),
      windSpeed: data.wind.speed,
      precipitation: isRaining
        ? (data.rain?.['1h'] ?? 0)
        : isSnowing
          ? (data.snow?.['1h'] ?? 0)
          : 0,
      humidity: data.main.humidity,
      cloudCover: data.clouds.all,
      sunrise: formatTimeStr(data.sys.sunrise),
      sunset: formatTimeStr(data.sys.sunset),
      isRaining: isRaining || false,
      isSnowing: isSnowing || false,
    };
  }

  /**
   * Public method to get weather for the application
   */
  async getCurrentWeather(
    coordinates: Coordinates = ENGELBERG_COORDINATES
  ): Promise<CurrentWeather> {
    try {
      // Fetch weather data
      const weatherData = await this.getCurrentWeatherData(coordinates);

      if (!weatherData) {
        console.error('Failed to fetch weather data, using fallback');
        // Return fallback data if API fails
        return this.getFallbackWeather();
      }

      // Map API response to our app's format
      return this.mapToCurrentWeather(weatherData);
    } catch (error) {
      console.error('Error getting current weather:', error);
      // Return fallback data on error
      return this.getFallbackWeather();
    }
  }

  /**
   * Provide fallback weather data when API calls fail
   */
  private getFallbackWeather(): CurrentWeather {
    const now = new Date();
    const month = now.getMonth();

    // Generate season-appropriate weather
    let condition: WeatherCondition = 'sunny';
    let temperature = 20;

    // Adjust based on season (similar to the mock implementation)
    if (month >= 2 && month <= 4) {
      // Spring
      condition = Math.random() > 0.5 ? 'sunny' : 'cloudy';
      temperature = 10 + Math.floor(Math.random() * 10);
    } else if (month >= 5 && month <= 7) {
      // Summer
      condition = Math.random() > 0.7 ? 'cloudy' : 'sunny';
      temperature = 18 + Math.floor(Math.random() * 10);
    } else if (month >= 8 && month <= 10) {
      // Autumn
      condition = Math.random() > 0.5 ? 'cloudy' : 'rainy';
      temperature = 10 + Math.floor(Math.random() * 8);
    } else {
      // Winter
      condition = Math.random() > 0.6 ? 'snowy' : 'cloudy';
      temperature = -5 + Math.floor(Math.random() * 10);
    }

    return {
      condition,
      temperature,
      windSpeed: 5 + Math.floor(Math.random() * 20),
      precipitation:
        condition === 'rainy' ? 2 + Math.floor(Math.random() * 10) : 0,
      humidity: 40 + Math.floor(Math.random() * 40),
      cloudCover:
        condition === 'sunny'
          ? 10 + Math.floor(Math.random() * 20)
          : 60 + Math.floor(Math.random() * 40),
      sunrise: '06:30',
      sunset: '20:30',
      isRaining: condition === 'rainy',
      isSnowing: condition === 'snowy',
    };
  }

  /**
   * Check if the service is ready to use
   */
  isReady(): boolean {
    return this.activeProvider.isReady();
  }
}

// Export a singleton instance
export const weatherService = new WeatherApiService();